import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Bell, BellRing, List } from 'lucide-react';
import { useAlertStore } from '../../store/alertStore';
import { addToast } from '../../store/toastStore';
import { normalizeToBase } from '../../utils/symbolUtils';

interface AlertButtonProps {
  symbol: string;
}

export const AlertButton: React.FC<AlertButtonProps> = ({ symbol }) => {
  const [showManageMenu, setShowManageMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const {
    alerts,
    placementMode,
    togglePlacementMode,
    openAlertsPanel,
    fetchAlerts,
  } = useAlertStore();

  // Fetch alerts on mount and when symbol changes
  useEffect(() => {
    if (symbol) {
      fetchAlerts(symbol);
    }
  }, [symbol, fetchAlerts]);

  // Update menu position when it opens
  useEffect(() => {
    if (showManageMenu && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
    }
  }, [showManageMenu]);

  // Click outside to close menu
  useEffect(() => {
    if (!showManageMenu) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isOutsideContainer = containerRef.current && !containerRef.current.contains(target);
      const isOutsideMenu = menuRef.current && !menuRef.current.contains(target);

      if (isOutsideContainer && isOutsideMenu) {
        setShowManageMenu(false);
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showManageMenu]);

  // Count active (non-paused) alerts for current symbol
  const normalizedSymbol = useMemo(() => normalizeToBase(symbol), [symbol]);

  const activeAlertCount = useMemo(() =>
    alerts.filter(
      (a) => normalizeToBase(a.symbol) === normalizedSymbol && !a.paused
    ).length,
    [alerts, normalizedSymbol]
  );

  // Toggle placement mode - click on chart pane determines alert type
  const handleMainClick = useCallback(() => {
    togglePlacementMode();
    if (!placementMode) {
      addToast('info', '📍 Click price pane or volume pane to set alert');
    }
  }, [togglePlacementMode, placementMode]);

  // Right-click or long-press to show manage menu
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setShowManageMenu(true);
  }, []);

  const handleManageAlerts = useCallback(() => {
    openAlertsPanel();
    setShowManageMenu(false);
  }, [openAlertsPanel]);

  return (
    <div ref={containerRef} className="alert-btn-container">
      {/* Main Alert Button - toggle placement mode */}
      <button
        className={`alert-btn-summary ${placementMode ? 'active' : ''}`}
        onClick={handleMainClick}
        onContextMenu={handleContextMenu}
        title={placementMode ? 'Click to exit alert placement' : 'Click to place alert (right-click to manage)'}
      >
        {placementMode ? <BellRing size={12} /> : <Bell size={12} />}
        {activeAlertCount > 0 && (
          <span className="alert-btn-count">{activeAlertCount}</span>
        )}
        {placementMode && (
          <span className="alert-btn-mode">Place</span>
        )}
      </button>

      {/* Manage button - always visible when there are alerts */}
      {alerts.length > 0 && !placementMode && (
        <button
          className="alert-btn-manage"
          onClick={handleManageAlerts}
          title="Manage all alerts"
        >
          <List size={10} />
        </button>
      )}

      {/* Context menu portal */}
      {showManageMenu && createPortal(
        <div
          ref={menuRef}
          className="alert-context-menu"
          style={{
            position: 'fixed',
            top: menuPosition.top,
            left: menuPosition.left,
            zIndex: 1000,
          }}
        >
          <button className="alert-context-item" onClick={handleManageAlerts}>
            <List size={14} />
            <span>Manage All Alerts</span>
            {alerts.length > 0 && (
              <span className="alert-context-count">{alerts.length}</span>
            )}
          </button>
        </div>,
        document.body
      )}
    </div>
  );
};

export default AlertButton;
